import type { CommunicationLogPlacementDto } from "@/api/communications";
import { ownerTypeLabel } from "@/api/communications";
import { cn } from "@/design-system/lib/utils";

interface Props {
  placement: CommunicationLogPlacementDto;
  className?: string;
  /** When true, renders as a non-link span (use inside list rows that
   *  are themselves wrapped by a navigating &lt;Link&gt;). */
  asStatic?: boolean;
}

/**
 * Placed-entity chip. When the backend resolves a DetailUrl the chip
 * navigates to the entity in a new tab — keeping the comm log entry
 * visible — matching the chip behaviour in the Internal Messaging
 * Integration Standard §6.4. Otherwise it renders the type+id pair as
 * a static label so a placement remains visible even before a detail
 * page exists for the underlying owner type.
 *
 * The X removal control is rendered alongside the chip by the parent
 * (PlacementList) — never inside the link — so a click on the X never
 * triggers chip navigation.
 */
export function PlacementChip({ placement, className, asStatic }: Props) {
  const typeLabel = ownerTypeLabel(placement.ownerType);
  // Prefer the server-resolved display label; fall back to a clean
  // "Type · id" render so the chip never shows a raw "deal:guid" colon
  // pair to the user.
  const display = placement.displayLabel ?? placement.ownerId;
  const tooltipBase = placement.displayLabel ?? `${typeLabel} · ${placement.ownerId}`;

  const inner = (
    <>
      <span className="font-display text-[9px] font-bold uppercase tracking-[0.1em] text-gold-shadow">
        {typeLabel}
      </span>
      <span className="text-small text-onyx truncate max-w-[12rem]">
        {display}
      </span>
    </>
  );

  const cls = cn(
    "inline-flex items-center gap-space-2 px-space-2 py-[3px] rounded-md",
    "border border-border-light bg-white-warm",
    "transition-colors duration-fast ease-standard",
    className,
  );

  if (placement.detailUrl && !asStatic) {
    return (
      <a
        href={placement.detailUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(cls, "hover:bg-gold/10 no-underline text-inherit")}
        title={`${tooltipBase} (opens in new tab)`}
      >
        {inner}
      </a>
    );
  }
  return (
    <span className={cls} title={tooltipBase}>
      {inner}
    </span>
  );
}
